import { db } from "@/lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import { logError } from "@/lib/bugsnag";

export interface OwnerProfile {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  phoneNumber?: string;
  pronouns?: string;
}

type OwnersMap = Record<string, { name?: string }>;

/**
 * Format an entity's `owners` map into a "Managed by …" label for artist cards.
 * Excludes the current user, so it returns "" when the artist is unowned or the
 * only owner is the current user.
 */
export function formatArtistOwnersLabel(
  owners: OwnersMap | undefined | null,
  currentUid?: string | null
): string {
  if (owners && typeof owners === "object") {
    const ownerNames = Object.entries(owners)
      .filter(([uid, o]) => uid !== currentUid && o.name)
      .map(([, o]) => o.name as string);
    if (ownerNames.length > 0) {
      return `Managed by ${ownerNames.join(", ")}`;
    }
  }

  return "";
}

/**
 * Resolve an entity's `owners` map (uid -> { name }) into displayable profiles
 * by looking each uid up in the users collection (the user doc id IS the uid).
 * Falls back to the name stored on the owners map when the user hasn't
 * registered yet (e.g. an invited-but-unclaimed owner). Lookups run in parallel.
 */
export async function resolveOwnerProfiles(
  owners: Record<string, unknown> | undefined | null,
  opts: {
    fallbackName?: string;
    /** Shown when the user doc exists but has no email. */
    missingEmail?: string;
    /** Shown when no user doc exists yet (unregistered/unclaimed). */
    pendingEmail?: string;
  } = {}
): Promise<OwnerProfile[]> {
  if (!owners) return [];

  const fallbackName = opts.fallbackName ?? "Unknown User";
  const missingEmail = opts.missingEmail ?? "No email available";
  const pendingEmail = opts.pendingEmail ?? missingEmail;

  return Promise.all(
    Object.entries(owners).map(async ([uid, data]) => {
      const storedName = (data as { name?: string }).name || fallbackName;
      try {
        const userSnap = await getDoc(doc(db, "users", uid));
        if (userSnap.exists()) {
          const userData = userSnap.data();
          return {
            id: uid,
            name: userData.name || storedName,
            email: userData.email || missingEmail,
            // The placeholder path is the "no image" default written by
            // useUser; treat it as absent so the initial shows instead.
            avatar:
              userData.avatar && userData.avatar !== "/img/icons/icon-profile.svg"
                ? userData.avatar
                : undefined,
            phoneNumber: userData.phoneNumber || undefined,
            pronouns: userData.pronouns || undefined,
          };
        }
        return { id: uid, name: storedName, email: pendingEmail };
      } catch (error) {
        logError(error, { context: "owners:resolveOwnerProfiles", uid });
        return { id: uid, name: storedName, email: missingEmail };
      }
    })
  );
}
